import { VideoJSPlayer } from '../VideoPlayer';
import { FaEye, FaEyeSlash, FaCalendarAlt, FaRegCalendarCheck } from 'react-icons/fa';
import { MdLocalHospital } from 'react-icons/md';
import { VideoEditBasicInfoSectionProps } from './types';
import { useVideoEditBasicInfoSection } from './useVideoEditBasicInfoSection';
import styles from './VideoEditBasicInfoSection.module.css';

/**
 * 動画編集画面の基本情報セクションコンポーネント
 */
const VideoEditBasicInfoSection = ({
  video,
  title,
  setTitle,
  description,
  setDescription,
  visibility,
  setVisibility,
}: VideoEditBasicInfoSectionProps) => {
  const {
    isPortrait,
    error,
    playerRef,
    videoUrl,
    isMobile,
    showPreview,
    setShowPreview,
    handleVideoReady,
    handleError,
    formatDate,
    convertUrlsToLinks,
  } = useVideoEditBasicInfoSection(video);

  // 動画プレーヤーをレンダリングする関数
  const renderVideoPlayer = () => {
    if (!video || !video.file_path || !videoUrl) return null;
    
    return (
      <div className={styles['video-edit-basic-info-section__video-container']}>
        <div 
          className={
            isPortrait 
              ? `${styles['video-edit-basic-info-section__video-player-wrapper']} ${styles['video-edit-basic-info-section__video-player-wrapper--portrait']}` 
              : styles['video-edit-basic-info-section__video-player-wrapper']
          }
        >
          <VideoJSPlayer
            videoId={`edit-${video.id}`}
            videoUrl={videoUrl}
            className={styles['video-edit-basic-info-section__react-player']}
            initialTime={0}
            autoPlay={false}
            muted={true}
            onReady={(player: any) => {
              console.log('VideoJS編集プレビュープレイヤー準備完了:', player);
              
              // ReactPlayer互換のプレーヤーオブジェクトを作成
              const reactPlayerCompatible = {
                // 基本制御
                play: () => player.play(),
                pause: () => player.pause(),
                
                // 時間制御
                seekTo: (time: number) => player.seekTo(time),
                getCurrentTime: () => player.getCurrentTime(),
                getDuration: () => player.getDuration(),
                
                // 音量制御
                setVolume: (volume: number) => player.setVolume(volume),
                getVolume: () => player.getVolume(),
                setMuted: (muted: boolean) => player.setMuted(muted),
                getMuted: () => player.getMuted(),
                
                // 内部プレイヤー取得
                getInternalPlayer: () => player.getInternalPlayer(),
                
                // バッファ情報
                getSecondsLoaded: () => {
                  const internalPlayer = player.getInternalPlayer();
                  if (internalPlayer && internalPlayer.buffered) {
                    const buffered = internalPlayer.buffered();
                    return buffered.length > 0 ? buffered.end(buffered.length - 1) : 0;
                  }
                  return 0;
                }
              };
              
              // playerRefに代入
              if (playerRef && typeof playerRef === 'object' && 'current' in playerRef) {
                playerRef.current = reactPlayerCompatible as any;
              }
              
              // onReadyコールバック実行
              handleVideoReady(reactPlayerCompatible as any);
            }}
            onError={(error: any) => {
              console.error('VideoJS編集プレビューエラー:', error);
              handleError(error);
            }}
            data-testid="video-edit-basic-info-section__player"
          />
        </div>
        {error && (
          <div className={styles['video-edit-basic-info-section__video-error']}>
            <p>動画の読み込みに失敗しました: {error.toString()}</p>
          </div>
        )}
      </div>
    );
  };

  if (!video) return (
    <div className={styles['video-edit-basic-info-section__no-video']}>
      動画情報が読み込まれていません
    </div>
  );

  return (
    <div className={styles['video-edit-basic-info-section__container']}>
      {video && video.file_path && renderVideoPlayer()}
      
      <div className={styles['video-edit-basic-info-section__date-info-container']} 
        style={isMobile ? { flexDirection: 'column', gap: '12px' } : undefined}
      >
        <div className={styles['video-edit-basic-info-section__date-info']}
          style={isMobile ? { fontSize: '13px', flex: '1' } : undefined}
        >
          <div 
            className={styles['video-edit-basic-info-section__date-label']} 
            style={isMobile ? { width: '50px' } : undefined}
          >
            投稿日:
          </div>
          <div className={styles['video-edit-basic-info-section__date-value']}>
            {video.created_at ? formatDate(video.created_at) : '---'}
          </div>
        </div>
        <div className={styles['video-edit-basic-info-section__date-info']} 
          style={isMobile ? { fontSize: '13px', flex: '1' } : undefined}
        >
          <div 
            className={styles['video-edit-basic-info-section__date-label']} 
            style={isMobile ? { width: '50px' } : undefined}
          >
            公開日:
          </div>
          <div className={styles['video-edit-basic-info-section__date-value']}>
            {video.published_at ? formatDate(video.published_at) : '未公開'}
          </div>
        </div>
        
      </div>

      <div className={styles['video-edit-basic-info-section__form-group']}>
        <label 
          className={styles['video-edit-basic-info-section__label']} 
          htmlFor="video-title"
        >
          動画タイトル
        </label>
        <input
          id="video-title"
          data-testid="video-edit-basic-info-section__title-input"
          className={styles['video-edit-basic-info-section__input']}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="動画のタイトルを入力してください"
        />
      </div>
      
      <div className={styles['video-edit-basic-info-section__form-group']}>
        <label 
          className={styles['video-edit-basic-info-section__label']} 
          htmlFor="video-description"
        >
          説明文
        </label>
        <textarea
          id="video-description"
          data-testid="video-edit-basic-info-section__description-textarea"
          className={styles['video-edit-basic-info-section__textarea']}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="動画の内容や手術手技について詳しく説明してください"
        />

        {/* プレビュー切り替えボタン */}
        {description && (
          <div className={styles['video-edit-basic-info-section__preview-button-container']}>
            <button
              type="button"
              data-testid="video-edit-basic-info-section__preview-button"
              className={styles['video-edit-basic-info-section__preview-button']}
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? 'プレビューを閉じる' : 'プレビューを表示'}
            </button>
          </div>
        )}

        {/* 説明文のプレビュー表示 (showPreviewがtrueの場合のみ表示) */}
        {description && showPreview && (
          <div 
            data-testid="video-edit-basic-info-section__preview-container"
            className={styles['video-edit-basic-info-section__preview-container']}
          >
            <div className={styles['video-edit-basic-info-section__preview-header']}>
              プレビュー（URLはリンクとして表示されます）:
            </div>
            <div>{convertUrlsToLinks(description)}</div>
          </div>
        )}
      </div>
      
      <div className={styles['video-edit-basic-info-section__form-group']}>
        <label className={styles['video-edit-basic-info-section__label']}>公開設定</label>
        <div className={styles['video-edit-basic-info-section__visibility-toggle']}>
          <div 
            className={`${styles['video-edit-basic-info-section__visibility-option']} ${
              visibility === 'public' ? styles['video-edit-basic-info-section__visibility-option--active'] : ''
            }`}
            data-testid="video-edit-basic-info-section__visibility-public"
            onClick={() => setVisibility('public')}
          >
            <FaEye className={styles['video-edit-basic-info-section__visibility-icon']} />
            外部公開
          </div>
          <div
            className={`${styles['video-edit-basic-info-section__visibility-option']} ${
              visibility === 'hospital_only' ? styles['video-edit-basic-info-section__visibility-option--active'] : ''
            }`}
            data-testid="video-edit-basic-info-section__visibility-hospital-only"
            onClick={() => setVisibility('hospital_only')}
          >
            <MdLocalHospital className={styles['video-edit-basic-info-section__visibility-icon']} />
            院内公開
          </div>
          <div 
            className={`${styles['video-edit-basic-info-section__visibility-option']} ${
              visibility === 'private' ? styles['video-edit-basic-info-section__visibility-option--active'] : ''
            }`}
            data-testid="video-edit-basic-info-section__visibility-private"
            onClick={() => setVisibility('private')}
          >
            <FaEyeSlash className={styles['video-edit-basic-info-section__visibility-icon']} />
            非公開
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoEditBasicInfoSection; 
